import styled from "styled-components";
import { useCustomer } from "../../../auth/CustomerProvider";
import { Accordion } from "@teamscheme/accordion";
import { useContract } from "../../ContractContext";
import { ContractType } from "../../model/ContractViewModel";
import ComparisonTable from "../ComparisonTable";
import {
  GetContractUpgradeProductsResponse,
  useContractUpgrade,
} from "../ContractTypePage";
import MemberUpgradeText from "../MemberUpgradeText";
import { SimplePackageDetails } from "../SimpleCard";
import { StandardPackageDetails, StandardUpgradeCard } from "../StandardCard";
import {
  CompletedMembershipUpgrade,
  CustomerMembershipUpgrade,
} from "./MembershipUpgrade";
import { Loading } from "./UpgradeContractModal";
import ErrorMessage from "../../../shared/error/ErrorMessage";
import { TextLink } from "@teamscheme/text-link";
import { PremiumPackageDetails, PremiumUpgradeCard } from "../PremiumCard";
import {
  UPGRADED_TO_STANDARD_TITLE,
  UpgradedToStandardIngress,
  UPGRADED_TO_COMPLETE_TITLE,
  UpgradedToCompleteIngress,
} from "./UpgradedText";
import { ImmutableClient } from "../../../api/ContractClient";

const Section = styled.section`
  &:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacing.space64};
  }
`;

export interface ModalContentProps {
  closeModal(): void;
}

export interface ModalContentInnerProps extends ModalContentProps {
  upgrade: GetContractUpgradeProductsResponse;
}

export interface ModalContentOuterProps extends ModalContentProps {
  upgrade: ImmutableClient<GetContractUpgradeProductsResponse>;
}

export function UpgradeModalContent(props: ModalContentProps) {
  const upgrade = useContractUpgrade();

  if (upgrade.isLoading) return <Loading />;
  if (upgrade.error || !upgrade.result) {
    return (
      <ErrorMessage>
        Vi kunne ikke hente informasjon om oppgraderinger akkurat nå.
        <br />
        <TextLink onClick={() => upgrade.refetch()}>Prøv igjen</TextLink>
      </ErrorMessage>
    );
  }

  return <UpgradeModalContentInner {...props} upgrade={upgrade.result} />;
}

export function UpgradeModalContentOuter({
  upgrade,
  ...props
}: ModalContentOuterProps) {
  if (upgrade.isLoading) return <Loading />;
  if (upgrade.error || !upgrade.result) {
    return (
      <ErrorMessage>
        Vi kunne ikke hente informasjon om oppgraderinger akkurat nå.
        <br />
        <TextLink onClick={() => upgrade.refetch()}>Prøv igjen</TextLink>
      </ErrorMessage>
    );
  }

  return <UpgradeModalContentInner {...props} upgrade={upgrade.result} />;
}

export function UpgradeModalContentInner({
  closeModal,
  upgrade,
}: ModalContentInnerProps) {
  const contract = useContract();
  const customer = useCustomer();

  if (customer.isLoading) return <Loading />;

  if (contract.contractType?.current === ContractType.Member) {
    return (
      <>
        <h1>Medlemskontrakt</h1>
        <CompletedMembershipUpgrade
          customerId={contract.contractType.upgradedCustomerId!}
        />
      </>
    );
  }

  if (customer.result?.qualifiesForMembershipUpgrade) {
    return (
      <>
        <h1>Oppgrader kontrakten</h1>
        <CustomerMembershipUpgrade
          customer={customer.result}
          closeModal={closeModal}
        />
      </>
    );
  }

  if (contract.contractType?.current === ContractType.Premium) {
    return (
      <>
        <h1>{UPGRADED_TO_COMPLETE_TITLE}</h1>
        <UpgradedToCompleteIngress />
      </>
    );
  }

  const canPurchase = (type: ContractType) =>
    upgrade.upgrades.find((x) => x.product.contractType === type)?.canPurchase;

  return (
    <div>
      {contract.contractType?.current === ContractType.Standard ? (
        <>
          <h1>{UPGRADED_TO_STANDARD_TITLE}</h1>
          <UpgradedToStandardIngress />
          <h2>Oppgrader kontrakten</h2>
        </>
      ) : (
        <h1>Oppgrader kontrakten</h1>
      )}
      <Section>
        <p>
          <MemberUpgradeText />
        </p>
        {canPurchase(ContractType.Premium) ? (
          <PremiumUpgradeCard upgrade={upgrade} onOpenPayment={closeModal} />
        ) : null}
        {canPurchase(ContractType.Standard) ? (
          <StandardUpgradeCard upgrade={upgrade} onOpenPayment={closeModal} />
        ) : null}
      </Section>
      <Section>
        <h3>Sammenlign våre oppgraderinger</h3>
        <ComparisonTable />
      </Section>
      <Section>
        <Accordion>
          <h3>Informasjon om pakkene</h3>
          <Accordion.Row id="trygt-eierskifte" title="Premium">
            <PremiumPackageDetails />
          </Accordion.Row>
          <Accordion.Row id="standard" title="Standard">
            <StandardPackageDetails />
          </Accordion.Row>
          <Accordion.Row id="simple" title="Enkel">
            <SimplePackageDetails />
          </Accordion.Row>
        </Accordion>
      </Section>
    </div>
  );
}
